import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import PermissionsGate from '../../components/PermissionGate'
import useReport from '../../hooks/reports/useReport'
import useMe from '../../hooks/users/useMe'
import { formatDate, addAllOption, formatReportColumns, getReportScope, filterOrganizations, isValidDate } from '../../utils/helpers'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import { Link as RouterLink } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import DateRangeIcon from '@mui/icons-material/DateRange'
import Popover from '@mui/material/Popover'
import { DateRange } from 'react-date-range'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { testTypes } from '../../utils/testTypes'
import useOrganizations from '../../hooks/kpis/useOrganizations'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import useLaboratories from '../../hooks/laboratories/useLaboratories'
import Button from '@mui/material/Button'
import { format, isBefore, isValid, isSameDay } from 'date-fns'
import useGenerateReport from '../../hooks/reports/useGenerateReport'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TablePagination from '@mui/material/TablePagination'
import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import { CSVLink } from 'react-csv'
import { useStore } from '../../hooks/globals/useStore'

const ALL_ORGANIZATION_OPTION = {
  Id: -1,
  Name: 'All',
  OrganizationCode: 'All'
}

const defaultFilterState = {
  state: 'All',
  organizations: [ALL_ORGANIZATION_OPTION],
  patient: '',
  dateRange: '',
  startDate: new Date(),
  endDate: new Date(),
  lab: 'All',
  testType: 'All',
}

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme()

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1)
  }

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1)
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
    </Box>
  )
}

TablePaginationActions.propTypes = {
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func
}

const Report = () => {
  const { id } = useParams()

  const [filters, setFilters] = useState(defaultFilterState)
  const [reset, setReset] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [displayCalendar, setDisplayCalendar] = useState(false)
  const [dateRangeInvalid, setDateRangeInvalid] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const { timezone } = useStore((state) => ({
    timezone: state.timezone
  }))

  useEffect(() => {
    if (id) {
      const reportId = parseInt(id, 10)
      setFilters((ps) => ({ ...ps, state: reportId >= 3 && reportId <= 6 ? 'CO' : 'All' }))
    }
  }, [id])

  const csvLink = useRef()

  const { data: report, isSuccess, isError, refetch: refetchReport, isStale, isLoading } = useReport(id)
  const { data: me } = useMe()
  const { data: organizations, refetch: fetchOrganizations, isStale: organizationsAreStale, isSuccess: gotOrganizations } = useOrganizations()
  const { data: laboratories, refetch: fetchLaboratories, isStale: laboratoriesAreStale, isSuccess: gotLaboratories } = useLaboratories()
  const { data: results, refetch: fetchResults, isSuccess: gotResults, isError: resultsError, isLoading: loadingResults } = useGenerateReport(id, page, rowsPerPage, filters, timezone)

  useEffect(() => {
    if (isStale) {
      refetchReport()
    }
  }, [isStale])

  useEffect(() => {
    if (organizationsAreStale) {
      fetchOrganizations()
    }
  }, [organizationsAreStale])

  useEffect(() => {
    if (report && report.ShowLab && laboratoriesAreStale) {
      fetchLaboratories()
    }
  }, [report, laboratories])

  const handleOpenCalendar = (e) => {
    setAnchorEl(e.currentTarget)
    setDisplayCalendar(true)
  }

  const handleCloseCalendar = () => {
    setDisplayCalendar(false)
    setAnchorEl(null)
  }

  const handleDateRangeChange = (event) => {
    const inputValue = event.target.value
    const { startDate, endDate } = processInputValue(inputValue)

    setFilters((ps) => ({
      ...ps,
      startDate,
      endDate,
      dateRange: inputValue
    }))
  }

  const processInputValue = (value) => {
    let [startDate, endDate] = value.split('-').map(elm => elm.trim())

    startDate = new Date(startDate)
    startDate = isValidDate(startDate) ? startDate : undefined

    endDate = new Date(endDate)
    endDate = isValid(endDate) ? endDate: undefined

    return { startDate, endDate }
  }

  const handleSelectDateRange = ({ selection }) => {
    let { startDate, endDate } = selection

    startDate = new Date(startDate)
    startDate = isValid(startDate) ? startDate : undefined

    endDate = new Date(endDate)
    endDate = isValid(endDate) ? endDate : undefined

    let inputValue = ''
    if (startDate) inputValue += format(startDate, 'MM/dd/yyyy')
    if (endDate) inputValue += ' - ' + format(endDate, 'MM/dd/yyyy')

    setFilters((ps) => ({
      ...ps,
      startDate,
      endDate,
      dateRange: inputValue
    }))
  }

  const handleChangeOrganizations = (event, newValue) => {
    if (newValue.length === 0) {
      setFilters((ps) => ({
        ...ps,
        organizations: [ALL_ORGANIZATION_OPTION]
      }))
    } else {
      const allIsFirst = newValue[0].OrganizationCode === 'All'
      if (allIsFirst) {
        const newValues = newValue.filter((v) => v.OrganizationCode !== 'All')
        setFilters((ps) => ({
          ...ps,
          organizations: newValues
        }))
      } else {
        const allIsLast = newValue[newValue.length - 1].OrganizationCode === 'All'
        if (allIsLast) {
          setFilters((ps) => ({
            ...ps,
            organizations: [ALL_ORGANIZATION_OPTION]
          }))
        } else {
          setFilters((ps) => ({
            ...ps,
            organizations: newValue
          }))
        }
      }
    }
  }

  const handleResetFilters = () => {
    setFilters(defaultFilterState)
    setReset(true)
    setDateRangeInvalid(false)
  }

  const handleGenerateReport = () => {
    setPage(0)
    if (reset) {
      setReset(false)
    }
    if (filters.dateRange) {
      const dates = filters.dateRange.split(' - ')
      const startDate = new Date(dates[0])
      const endDate = new Date(dates[1])
      if (isValidDate(dates[0]) && isValidDate(dates[1]) && (isBefore(startDate, endDate) || isSameDay(startDate, endDate))) {
        fetchResults()
      } else {
        setDateRangeInvalid(true)
      }
    } else {
      fetchResults()
    }
  }

  useEffect(() => {
    if (dateRangeInvalid && filters.dateRange) {
      const dates = filters.dateRange.split(' - ')
      const startDate = new Date(dates[0])
      const endDate = new Date(dates[1])
      if (isValidDate(dates[0]) && isValidDate(dates[1]) && (isBefore(startDate, endDate) || isSameDay(startDate, endDate))) {
        setDateRangeInvalid(false)
      }
    }
  }, [dateRangeInvalid, filters.dateRange])


  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    if (gotResults && page && rowsPerPage) {
      fetchResults()
    }
  }, [gotResults, page, rowsPerPage])

  const emptyRows = gotResults ?
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - results.total) : 0
    : 0

  return (
    <PermissionsGate
      user={me}
      scopes={getReportScope(id)}
      RenderError={() => <Alert severity='error'>Unable to load report.</Alert>}
    >
      {
        isLoading && (
          <div>
            <CircularProgress />
            Loading Report...
          </div>
        )
      }
      {
        isError && (
          <Alert severity='error'>
            Unable to load report.
          </Alert>
        )
      }
      {
        isSuccess && (
          <>
            <Breadcrumbs>
              <Link
                component={RouterLink}
                to='/reports'
                underline='hover'
              >
                <Typography
                  variant='h4'
                  sx={{
                    color: '#27417A',
                    fontWeight: 'bold'
                  }}
                >
                  Reports
                </Typography>
              </Link>
              <Typography
                variant='h5'
                sx={{
                  color: '#3B6DAA',
                  fontWeight: 'bold'
                }}
              >
                {report?.Name}
              </Typography>
            </Breadcrumbs>
            <Paper
              sx={{ mt: 2, mb: 2, p: 2, border: 'solid 3px #27417A' }}
              elevation={0}
            >
              <Typography
                variant='h5'
                sx={{
                  color: '#3B6DAA',
                  fontWeight: 'bold'
                }}
              >
                Report Filters
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid
                  container
                  spacing={2}
                >
                  {
                    report.ShowPatientSearch && (
                      <Grid
                        item
                        md={4}
                        xs={12}
                      >
                        <TextField
                          label='Patient Search'
                          fullWidth
                          size='small'
                          value={filters.patient}
                          onChange={(event) => setFilters((previousFilters) => ({ ...previousFilters, patient: event.target.value }))}
                        />
                      </Grid>
                    )
                  }
                  {
                    report.ShowTestType && (
                      <Grid
                        item
                        md={4}
                        xs={12}
                      >
                        <FormControl fullWidth size='small'>
                          <InputLabel id='test-type-label'>Test Type</InputLabel>
                          <Select
                            labelId='test-type-label'
                            id='test-type'
                            label='Test Type'
                            value={filters.testType}
                            onChange={(event) => setFilters((ps) => ({ ...ps, testType: event.target.value }))}
                          >
                            <MenuItem value='All'>All</MenuItem>
                            {
                              testTypes.map((testType) => (
                                <MenuItem
                                  key={testType.value}
                                  value={testType.value}
                                >
                                  {testType.value}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Grid>
                    )
                  }
                  {
                    report.ShowTestResultDateRange && (
                      <Grid
                        item
                        md={4}
                        xs={12}
                      >
                        <TextField
                          label='Test Result Date Range'
                          fullWidth
                          size='small'
                          value={filters.dateRange}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position='end'>
                                <IconButton onClick={handleOpenCalendar}>
                                  <DateRangeIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          onChange={handleDateRangeChange}
                          error={dateRangeInvalid}
                          helperText={dateRangeInvalid ? 'Please enter valid date range using this format MM/DD/YYYY - MM/DD/YYYY' : ''}
                        />
                        <Popover
                          open={displayCalendar}
                          anchorEl={anchorEl}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                          }}
                          onClose={handleCloseCalendar}
                        >
                          <Box>
                            <DateRange
                              ranges={[{
                                startDate: filters.startDate,
                                endDate: filters.endDate,
                                key: 'selection'
                              }]}
                              rangeColors={['#3A6DAA']}
                              onChange={handleSelectDateRange}
                              maxDate={new Date()}
                              showMonthAndYearPickers
                              moveRangeOnFirstSelection={false}
                              showDateDisplay={false}
                            />
                          </Box>
                        </Popover>
                      </Grid>
                    )
                  }
                  {
                    report.ShowState && (
                      <Grid
                        item
                        md={4}
                        xs={12}
                      >
                        <FormControl fullWidth size='small'>
                          <InputLabel id="state-select-label">State</InputLabel>
                          <Select
                            labelId="state-select-label"
                            id="state-select"
                            value={filters.state}
                            label="State"
                            onChange={(event) => setFilters((ps) => ({ ...ps, state: event.target.value, organizations: [ALL_ORGANIZATION_OPTION] }))}
                          >
                            <MenuItem value='All'>All</MenuItem>
                            <MenuItem value='CO'>Colorado</MenuItem>
                            <MenuItem value='WI'>Wisconsin</MenuItem>
                            <MenuItem value='Other'>Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )
                  }
                  {
                    report.ShowLab && (
                      <Grid
                        item
                        md={6}
                        xs={12}
                      >
                        <FormControl fullWidth size='small'>
                          <InputLabel id="lab-select-label">Laboratory</InputLabel>
                          <Select
                            labelId="label-select-label"
                            id="lab-select"
                            value={filters.lab}
                            label="Laboratory"
                            onChange={(event) => setFilters((ps) => ({ ...ps, lab: event.target.value }))}
                          >
                            <MenuItem value='All'>All</MenuItem>
                            {
                              gotLaboratories && laboratories.map((lab) => (
                                <MenuItem key={`lab-${lab.Id}`} value={lab.Id}>
                                  {lab.Name}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Grid>
                    )
                  }
                  {
                    report.ShowOrganizations && (
                      <Grid
                        item
                        md={8}
                        xs={12}
                      >
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          id='tags-organizations'
                          options={gotOrganizations ? addAllOption(filterOrganizations(filters.state, organizations), ALL_ORGANIZATION_OPTION) : []}
                          getOptionLabel={(option) => option.Name}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.Name}
                            </li>
                          )}
                          value={filters.organizations}
                          onChange={handleChangeOrganizations}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Organizations'
                              size='small'
                            />
                          )}
                        />
                      </Grid>
                    )
                  }
                  <Grid
                    item
                    xs={12}
                    align='end'
                  >
                    {
                      results && results.all.length > 0 && !reset && (
                        <>
                          <Button
                            variant='contained'
                            sx={{
                              mr: 2
                            }}
                            color='success'
                            onClick={() => csvLink.current.link.click()}
                          >
                            Export Results
                          </Button>
                          <CSVLink 
                            data={results && results.all}
                            filename={`${report.Name.replace(' ', '_')}_${formatDate(new Date())}.csv`}
                            target='_blank'
                            ref={csvLink}
                            className='hidden'
                          />
                        </>
                      )
                    }
                    <Button
                      variant='contained'
                      color='secondary'
                      sx={{
                        mr: 2
                      }}
                      onClick={handleResetFilters}
                    >
                      Reset
                    </Button>
                    <Button 
                      variant='contained'
                      sx={{
                        backgroundColor: '#3A6DAA'
                      }}
                      onClick={handleGenerateReport}
                    >
                    Search
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
            <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
              {
                results === undefined || reset && (
                  <Alert severity='info'>
                    Please add filters to generate report
                  </Alert>
                )
              }
              {
                resultsError && (
                  <Alert severity='error'>
                    Unable to load report.
                  </Alert>
                )
              }
              {
                loadingResults && (
                  <div>
                    <CircularProgress />
                    Loading results...
                  </div>
                )
              }
              {
                gotResults && results.results.length > 0 && !reset && (
                  <TableContainer>
                    <Table
                      aria-labelledby='report-table'
                    >
                      <TableHead>
                        <TableRow>
                          {
                            formatReportColumns(results).map((column) => (
                              <TableCell key={column}>
                                {column}
                              </TableCell>
                            ))
                          }
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          results.results.map((row, index) => (
                            <TableRow
                              key={`report_${index}`}
                              hover
                              tabIndex={-1}
                            >
                              {
                                formatReportColumns(results).map((column) => (
                                  <TableCell key={`report_${index}_${column}`}>
                                    {row[column]}
                                  </TableCell>
                                ))
                              }
                            </TableRow>
                          ))
                        }
                        {
                          emptyRows > 0 && (
                            <TableRow
                              style={{
                                height: 53 * emptyRows
                              }}
                            >
                              <TableCell colSpan={formatReportColumns(results).length} />
                            </TableRow>
                          )
                        }
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component='div'
                      count={results.total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      ActionsComponent={TablePaginationActions}
                    />
                  </TableContainer>
                )
              }
              {
                results && results.results.length === 0 && !reset && (
                  <Alert severity='info'>
                    No results found
                  </Alert>
                )
              }
            </Box>
          </>
        )
      }
    </PermissionsGate>
  )
}

export default Report